import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, CornerDownLeft, X } from "lucide-react";
import { api } from "@/lib/api";
import { NAV } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Desktop search bar with an inline suggestions dropdown. Live-fetches
 * `/api/search?q=` (debounced 250ms) and shows top 3 hits per group.
 * Mirrors the UX of `MobileSearchOverlay` so users get the same feel on both.
 * Pressing Enter still lands on the full `/search` page.
 */
export default function DesktopSearchDropdown({ initialQuery = "" }) {
  const [q, setQ] = useState(initialQuery);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    const term = q.trim();
    if (!term) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    const timer = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(term)}`)
        .then(r => {
          const enabled = (r.data.groups || []).filter(g => g.enabled && g.results?.length);
          setGroups(enabled);
        })
        .catch(() => setGroups([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  // Close on outside click / Escape
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const term = q.trim();
    if (!term) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const total = groups.reduce((acc, g) => acc + g.results.length, 0);
  const hasQuery = q.trim().length > 0;

  return (
    <div ref={wrapRef} className="hidden lg:flex flex-1 items-center gap-2 max-w-2xl relative">
      <form onSubmit={submit} className="flex-1 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid={NAV.searchInput}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search Site"
            className="pl-9 border-slate-300"
            autoComplete="off"
          />
          {hasQuery && (
            <button type="button" onClick={() => { setQ(""); setGroups([]); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                    aria-label="Clear search">
              <X className="w-3.5 h-3.5"/>
            </button>
          )}
        </div>
        {hasQuery && (
          <Button data-testid={NAV.searchSubmit} type="submit" className="bg-sky-700 hover:bg-sky-600">
            Search
          </Button>
        )}
      </form>

      {open && hasQuery && (
        <div data-testid="desktop-search-dropdown"
             className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          <div className="p-3">
            {loading && total === 0 && (
              <p className="text-sm text-slate-400 py-4 text-center italic">Searching…</p>
            )}
            {!loading && total === 0 && (
              <p data-testid="desktop-search-empty" className="text-sm text-slate-400 py-4 text-center italic">
                No matches for &ldquo;{q}&rdquo;.
              </p>
            )}
            {groups.map(g => (
              <div key={g.key} className="mb-3 last:mb-0" data-testid={`desktop-search-group-${g.key}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 px-1">
                  {g.label || g.key}
                </div>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden">
                  {g.results.slice(0, 3).map(r => (
                    <li key={r.id}>
                      <Link to={r.href}
                            data-testid={`desktop-search-hit-${g.key}-${r.id}`}
                            onClick={() => setOpen(false)}
                            className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 mt-1 shrink-0"/>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100 line-clamp-1">{r.title}</div>
                          {r.subtitle && (
                            r.subtitle_html
                              ? <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 [&_mark]:bg-yellow-100 [&_mark]:text-slate-900 [&_mark]:rounded [&_mark]:px-0.5"
                                     dangerouslySetInnerHTML={{__html: r.subtitle}}/>
                              : <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{r.subtitle}</div>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {total > 0 && (
              <button data-testid="desktop-search-all"
                      onClick={submit}
                      className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-sky-700 hover:text-sky-900 dark:text-sky-400 py-2.5 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors mt-1">
                See all results for &ldquo;{q}&rdquo;
                <CornerDownLeft className="w-4 h-4"/>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
